/**
 * Event bridge between non-UI code (API service, MCP handlers, anywhere else
 * that can't import a UI store) and the UpgradePromptModal.
 *
 * Rationale: the HTTP service layer must not depend on a Zustand UI store.
 * Direct `useUpgradePromptStore.getState().open(...)` calls from api.service
 * couple the service to React state management, which breaks SSR, unit
 * tests, and future non-React consumers (e.g. a worker calling the service).
 *
 * Contract:
 *   - api.service calls `emitUpgradeGateBlocked(gateKey, context)` on 402.
 *   - UpgradePromptModal subscribes on mount via `subscribeUpgradeGateBlocked`
 *     and forwards the event to the Zustand store.
 *
 * Any environment without `window` (SSR, Node-based tests) silently no-ops
 * rather than throwing — the store subscription simply never fires.
 */

import type { GateKey, GateContext } from '@bower/shared';

const EVENT_NAME = 'bower:upgrade-gate-blocked';

export interface UpgradeGateBlockedDetail {
  readonly gateKey: GateKey;
  readonly context: GateContext;
}

// Custom event tag used on the window bus so TypeScript catches mismatches
// between emitters and subscribers.
type UpgradeGateBlockedEvent = CustomEvent<UpgradeGateBlockedDetail>;

export function emitUpgradeGateBlocked(gateKey: GateKey, context: GateContext = {}): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<UpgradeGateBlockedDetail>(EVENT_NAME, {
      detail: { gateKey, context },
    }),
  );
}

/**
 * Subscribes to upgrade-gate events. Returns a cleanup function suitable
 * for use in a React `useEffect` return.
 */
export function subscribeUpgradeGateBlocked(
  handler: (detail: UpgradeGateBlockedDetail) => void,
): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const listener = (event: Event) => {
    const custom = event as UpgradeGateBlockedEvent;
    handler(custom.detail);
  };
  window.addEventListener(EVENT_NAME, listener);
  return () => window.removeEventListener(EVENT_NAME, listener);
}
